import { CommonModule } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-admin-paginator',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @if (totalItems() > 0) {
      <div class="flex flex-col gap-3 border-t border-base-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex flex-wrap items-center gap-3 text-xs font-bold text-base-content/60">
          <span>Showing {{ firstItem() }}-{{ lastItem() }} of {{ totalItems() }}</span>
          @if (showPageSize()) {
            <label class="flex items-center gap-2">
              <span>Rows</span>
              <select
                class="select select-xs select-bordered"
                [ngModel]="pageSize()"
                (ngModelChange)="changePageSize($event)"
                aria-label="Rows per page"
              >
                @for (size of pageSizeOptions(); track size) {
                  <option [ngValue]="size">{{ size }}</option>
                }
              </select>
            </label>
          }
        </div>

        <div class="join self-end sm:self-auto" aria-label="Pagination">
          <button class="btn btn-sm join-item" type="button" aria-label="Previous page"
            [disabled]="currentPage() <= 1" (click)="goTo(currentPage() - 1)">
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          @for (page of pageNumbers(); track page) {
            <button class="btn btn-sm join-item" type="button"
              [class.btn-primary]="page === currentPage()"
              [class.btn-ghost]="page !== currentPage()"
              [attr.aria-current]="page === currentPage() ? 'page' : null"
              (click)="goTo(page)">{{ page }}</button>
          }
          <button class="btn btn-sm join-item" type="button" aria-label="Next page"
            [disabled]="currentPage() >= totalPages()" (click)="goTo(currentPage() + 1)">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    }
  `,
})
export class AdminPaginatorComponent {
  readonly totalItems = input.required<number>();
  readonly currentPage = input(1);
  readonly pageSize = input(10);
  readonly pageSizeOptions = input<number[]>([10, 25, 50]);
  readonly showPageSize = input(true);
  readonly pageChange = output<number>();
  readonly pageSizeChange = output<number>();

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalItems() / this.pageSize())),
  );
  readonly firstItem = computed(() =>
    this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1,
  );
  readonly lastItem = computed(() =>
    Math.min(this.currentPage() * this.pageSize(), this.totalItems()),
  );
  readonly pageNumbers = computed(() => {
    const total = this.totalPages();
    const start = Math.max(1, Math.min(this.currentPage() - 2, total - 4));
    const end = Math.min(total, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  });

  goTo(page: number): void {
    if (page >= 1 && page <= this.totalPages()) this.pageChange.emit(page);
  }

  changePageSize(value: unknown): void {
    const size = Number(value);
    if (this.pageSizeOptions().includes(size)) this.pageSizeChange.emit(size);
  }
}
